import React, { useState, useMemo, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  KeyboardAvoidingView,
  Platform,
  Alert,
  Modal,
  FlatList,
  ScrollView,
} from '../components/rn';
import PrimaryActionButton from '../components/PrimaryActionButton';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { spacing, fontSize, ThemeColors } from '../theme';
import { useTheme } from '../theme/ThemeContext';
import { useTranslation } from '../i18n/I18nContext';
import { api, ApiError, NetworkError } from '../services/api';
import { COUNTRIES, Country } from '../data/countries';
import { getPhoneCountryOptions } from '../data/phoneCountrySearch';
import { getDeviceRegion } from '../utils/locale';
import Logo from '../components/Logo';
import GoogleLoginAction from '../components/GoogleLoginAction';
import PasskeySignInAction from '../components/PasskeySignInAction';
import ProfileSection from '../components/ProfileSection';
import Ionicons from '@expo/vector-icons/Ionicons';
import { profileFieldStyle, profilePalette } from '../theme/profileSection';

// Cihazın bölgesine göre varsayılan ülke (küresel ürün — sabit +90 yok).
//
// D-427 — yedek artık "listenin ilki" değil. Liste alfabetik olduğu için ilk sıra
// rastgele bir ülkeye (Afganistan) denk gelirdi; öncesinde ise Türkiye'ye denk geliyordu ve
// küresel bir üründe tek bir pazarı varsayılan yapıyordu. Yedek açıkça şirketin yargı
// çevresidir (`LEGAL.md` — UK Ltd) ve yalnız cihaz bölgesi okunamadığında devreye girer.
const FALLBACK_ISO = 'GB';

function defaultCountry(): Country {
  const region = getDeviceRegion();
  return COUNTRIES.find((c) => c.iso === region)
    ?? COUNTRIES.find((c) => c.iso === FALLBACK_ISO)
    ?? COUNTRIES[0];
}

type Props = {
  onContinue: (phone: string) => void;
  onVerify?: (token: string, onboarded: boolean) => void | Promise<void>;
};

export default function PhoneScreen({ onContinue, onVerify }: Props) {
  const colors = useTheme();
  const insets = useSafeAreaInsets();
  const { t, lang } = useTranslation();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const [country, setCountry] = useState<Country>(defaultCountry);
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const activeMethod = useRef<'sms' | 'google' | 'passkey' | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Yerel numara sadece rakam; baştaki 0'lar atılır (uluslararası biçim).
  const localDigits = phone.replace(/\D/g, '').replace(/^0+/, '');
  const fullPhone = country.dial + localDigits;
  const canContinue = localDigits.length >= 6 && !loading && !googleBusy;

  async function handleContinue() {
    if (activeMethod.current) return;
    activeMethod.current = 'sms';
    setLoading(true);
    try {
      await api.sendOtp(fullPhone);
      onContinue(fullPhone);
    } catch (error) {
      if (error instanceof NetworkError) {
        Alert.alert(t('message.noConnection'), t('message.checkInternet'));
      } else {
        Alert.alert(t('common.error'), t(error instanceof ApiError && error.status === 429
          ? 'otp.rateLimited' : 'phone.sendFailed'));
      }
    } finally {
      activeMethod.current = null;
      setLoading(false);
    }
  }

  const filtered = useMemo(() => getPhoneCountryOptions(lang, search), [lang, search]);
  const countryName = useMemo(() => getPhoneCountryOptions(lang, '')
    .find((item) => item.iso === country.iso)?.localizedName ?? country.name, [lang, country]);

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
      <ScrollView
        contentContainerStyle={[styles.scrollContent, {
          paddingTop: insets.top + spacing.lg, paddingBottom: insets.bottom + spacing.lg,
        }]}
        keyboardShouldPersistTaps="handled"
        showsVerticalScrollIndicator={false}>
        <View style={styles.inner}>
        <View style={styles.brand}>
          <Logo size={64} color={profilePalette(colors).heading} />
          <Text style={styles.title}>nosubject</Text>
        </View>
        <ProfileSection testID="phone-sign-in" header={
          <Text style={styles.subtitle}>{t('phone.subtitle')}</Text>
        }>
        <View style={styles.phoneRow}>
          <TouchableOpacity
            testID="phone-country-picker"
            style={styles.countryBtn}
            accessibilityRole="button"
            accessibilityLabel={`${t('phone.selectCountry')}: ${countryName}, ${country.dial}`}
            accessibilityState={{ expanded: pickerOpen }}
            onPress={() => { setSearch(''); setPickerOpen(true); }}>
            <Text style={styles.countryFlag} accessible={false}>{country.flag}</Text>
            <Text style={styles.countryName}>{countryName}</Text>
            <Text style={styles.countryDial}>{country.dial}</Text>
            <Ionicons name="chevron-down-outline" size={18} color={profilePalette(colors).heading} accessible={false} />
          </TouchableOpacity>
          <TextInput
            testID="phone-number-input"
            style={styles.input}
            accessibilityLabel={t('phone.subtitle')}
            placeholder="555 123 4567"
            placeholderTextColor={colors.gray400}
            keyboardType="number-pad"
            value={phone}
            onChangeText={(v) => setPhone(v.replace(/[^0-9]/g, ''))}
            autoFocus
          />
        </View>

        <PrimaryActionButton
          testID="onboarding-phone-continue"
          appearance="profile"
          label={t('common.continue')}
          disabled={!canContinue}
          loading={loading}
          onPress={handleContinue}
        />
        <GoogleLoginAction onVerify={onVerify} disabled={loading}
          tryStart={() => {
            if (activeMethod.current) return false;
            activeMethod.current = 'google';
            setGoogleBusy(true);
            return true;
          }}
          onFinish={() => { activeMethod.current = null; setGoogleBusy(false); }} />
        {/* D-882 — Face ID, Touch ID or a fingerprint, through a passkey. */}
        <PasskeySignInAction onVerify={onVerify} disabled={loading}
          tryStart={() => {
            if (activeMethod.current) return false;
            activeMethod.current = 'passkey';
            setGoogleBusy(true);
            return true;
          }}
          onFinish={() => { activeMethod.current = null; setGoogleBusy(false); }} />
        </ProfileSection>
        </View>
      </ScrollView>

      {Platform.OS === 'web' && (
        <TouchableOpacity
          style={[styles.footer, { paddingBottom: insets.bottom + spacing.md }]}
          onPress={() => (globalThis as any).open?.('/mimari', '_blank')}>
          <Text style={styles.footerLink}>{t('phone.architectureLink')}</Text>
        </TouchableOpacity>
      )}

      <Modal visible={pickerOpen} animationType="slide" onRequestClose={() => setPickerOpen(false)}>
        <KeyboardAvoidingView
          style={[styles.modal, { paddingTop: insets.top + spacing.md }]}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>{t('phone.selectCountry')}</Text>
            <TouchableOpacity testID="phone-country-cancel" style={styles.modalCancelButton}
              accessibilityRole="button" onPress={() => setPickerOpen(false)}>
              <Text style={styles.modalCancel}>{t('common.cancel')}</Text>
            </TouchableOpacity>
          </View>
          <TextInput
            testID="phone-country-search"
            style={styles.modalSearch}
            placeholder={t('phone.countrySearch')}
            placeholderTextColor={colors.gray400}
            value={search}
            onChangeText={setSearch}
            autoCorrect={false}
            autoFocus
          />
          <FlatList
            data={filtered}
            keyExtractor={(item) => item.iso}
            keyboardShouldPersistTaps="handled"
            contentContainerStyle={{ paddingHorizontal: spacing.md, paddingBottom: insets.bottom + spacing.md }}
            renderItem={({ item }) => (
              <TouchableOpacity
                testID={`phone-country-${item.iso}`}
                style={styles.countryRow}
                accessibilityRole="button"
                accessibilityState={{ selected: item.iso === country.iso }}
                accessibilityLabel={`${item.localizedName}, ${item.dial}`}
                onPress={() => { setCountry(item); setPickerOpen(false); }}>
                <Text style={styles.rowFlag}>{item.flag}</Text>
                <Text style={styles.rowName}>{item.localizedName}</Text>
                <Text style={styles.rowDial}>{item.dial}</Text>
                {item.iso === country.iso && <Ionicons name="checkmark-outline" size={18}
                  color={profilePalette(colors).link} accessible={false} />}
              </TouchableOpacity>
            )}
            ItemSeparatorComponent={() => <View style={styles.separator} />}
          />
        </KeyboardAvoidingView>
      </Modal>
    </KeyboardAvoidingView>
  );
}

const makeStyles = (colors: ThemeColors) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.white,
  },
  scrollContent: { flexGrow: 1, justifyContent: 'center' },
  inner: {
    width: '100%', maxWidth: 480, alignSelf: 'center', minWidth: 0,
  },
  brand: { alignItems: 'center', paddingHorizontal: spacing.md, gap: spacing.sm },
  title: {
    fontSize: fontSize.xl,
    fontWeight: '600',
    color: profilePalette(colors).heading,
  },
  subtitle: {
    fontSize: fontSize.md,
    fontWeight: '600', lineHeight: 24,
    color: profilePalette(colors).heading,
    paddingVertical: spacing.sm,
  },
  phoneRow: {
    gap: spacing.sm, minWidth: 0,
    marginBottom: spacing.md,
  },
  countryBtn: {
    ...profileFieldStyle(colors),
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  countryFlag: { fontSize: fontSize.lg },
  countryName: { flex: 1, minWidth: 0, fontSize: fontSize.md, color: colors.black },
  countryDial: { flexShrink: 1, fontSize: fontSize.md, color: profilePalette(colors).heading, fontWeight: '600' },
  input: {
    ...profileFieldStyle(colors),
    fontSize: fontSize.md,
    color: colors.black,
  },
  footer: {
    paddingBottom: spacing.lg,
    alignItems: 'center',
  },
  footerLink: {
    fontSize: fontSize.sm,
    color: profilePalette(colors).link,
    textDecorationLine: 'underline',
  },
  modal: { flex: 1, backgroundColor: colors.white },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.md,
  },
  modalTitle: { flex: 1, minWidth: 0, marginRight: spacing.sm, fontSize: fontSize.lg, fontWeight: '600', color: profilePalette(colors).heading },
  modalCancelButton: { minHeight: 44, minWidth: 44, maxWidth: '45%', justifyContent: 'center', paddingHorizontal: spacing.sm },
  modalCancel: { fontSize: fontSize.md, color: profilePalette(colors).heading, textAlign: 'center' },
  modalSearch: {
    ...profileFieldStyle(colors),
    fontSize: fontSize.md,
    color: colors.black,
    marginHorizontal: spacing.md,
    marginBottom: spacing.sm,
  },
  countryRow: { minHeight: 48, minWidth: 0, flexDirection: 'row', alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.md },
  rowFlag: { fontSize: fontSize.lg },
  rowName: { flex: 1, minWidth: 0, fontSize: fontSize.md, color: colors.black },
  rowDial: { flexShrink: 1, fontSize: fontSize.md, color: colors.gray600, fontWeight: '500' },
  separator: { height: StyleSheet.hairlineWidth, backgroundColor: colors.gray200 },
});
